import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  input,
  InputSignal,
  OnInit,
  output,
  signal,
  viewChild,
  inject,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ActionButton } from '@shared/abstraction/grid/action-button';
import { SearchSetting } from '@shared/abstraction/grid/search-setting';
import { DataType } from '@shared/helpers/enums/data-type';
import { QueryOptions } from 'odata-query';
import {
  Client,
  ClientWithAssignments,
  Collection,
  GridItemCreateEvent,
  GridItemUpdateEvent,
} from '../../models';
import { AdminPrimeGridComponent } from '../admin-prime-grid/admin-prime-grid.component';
import { AssignClientComponent } from '../assign-client/assign-client.component';
import { AssignCountryMaterialComponent } from '../assign-country-material/assign-country-material.component';
import { UpsertClientComponent } from '../upsert-client/upsert-client.component';
import {
  DrawerParams,
  DrawerSetting,
  GridColumn,
} from '@shared/modules/grid/abstractions';

@Component({
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'gstar-clients-grid',
  templateUrl: 'clients-grid.component.html',
})
export class ClientsGridComponent implements OnInit {
  private translateService = inject(TranslateService);

  source: InputSignal<Collection<Client>> = input.required({});

  query = input<Partial<QueryOptions<Client>>>();
  queryWritable = signal<Partial<QueryOptions<Client>>>({});

  queryChange = output<Partial<QueryOptions<Client>>>();
  create = output<GridItemCreateEvent<ClientWithAssignments>>();
  update = output<GridItemUpdateEvent<ClientWithAssignments>>();
  assignUser = output<GridItemUpdateEvent<ClientWithAssignments>>();
  assignCountry = output<GridItemUpdateEvent<ClientWithAssignments>>();
  deleteRequested = output<Client>();

  drawerSetting: DrawerSetting<Client, ClientWithAssignments> = {
    create: {
      title: this.translateService.instant('admin.grid.createClientTitle'),
      component: UpsertClientComponent,
      buttons: [
        {
          label: this.translateService.instant('admin.grid.cancel'),
          command: (closeFn): void => {
            closeFn();
          },
          isPrimary: false,
        },
        {
          label: this.translateService.instant('admin.grid.create'),
          command: (closeFn, item): void => {
            if (item?.isValid) {
              this.create.emit({ item: item.model!, onSuccess: closeFn });
            }
          },
          isPrimary: true,
        },
      ],
    },
    edit: {
      title: this.translateService.instant('admin.grid.updateClientTitle'),
      component: UpsertClientComponent,
      buttons: [
        {
          label: this.translateService.instant('admin.grid.cancel'),
          command: (closeFn): void => {
            closeFn();
          },
          isPrimary: false,
        },
        {
          label: this.translateService.instant('admin.grid.update'),
          command: (closeFn, item): void => {
            if (item.isValid) {
              this.triggerEmit(item.model, item.prevModel, closeFn);
            }
          },
          isPrimary: true,
        },
      ],
    },
  };
  grid =
    viewChild<AdminPrimeGridComponent<Client, ClientWithAssignments>>('grid');

  actionMenu: ActionButton<Client>[] = [
    {
      label: this.translateService.instant('admin.client.updateClient'),
      icon: '',
      command: (item: Client): void => {
        this.updateClient(item);
      },
    },
    {
      label: this.translateService.instant('admin.client.manageAssignedUsers'),
      icon: '',
      command: (item: Client): void => {
        this.manageAssignedClients(item);
      },
    },
    {
      label: this.translateService.instant(
        'admin.client.manageCountryMaterials'
      ),
      icon: '',
      command: (item: Client): void => {
        this.manageCountryMaterials(item);
      },
    },
    {
      label: this.translateService.instant('admin.client.deleteClient'),
      icon: '',
      command: (item: Client): void => {
        this.deleteRequested.emit(item);
      },
    },
  ];

  headers: GridColumn[] = [
    {
      label: this.translateService.instant('client.name'),
      key: 'name',
      dataType: DataType.String,
      isSortable: true,
    },
    {
      label: this.translateService.instant('client.created'),
      key: 'created',
      dataType: DataType.Date,
      isSortable: true,
    },
    {
      label: this.translateService.instant('client.isActive'),
      key: 'isActive',
      dataType: DataType.Boolean,
      isSortable: true,
    },
  ];

  searchSetting: SearchSetting<Client> = {
    filtersLogicalOperator: 'or',
    filters: [
      {
        dataKey: 'name',
        operator: 'contains',
      },
    ],
  };

  ngOnInit(): void {
    this.queryWritable.set(this.query() ?? {});
  }

  queryHandler(securityTypeQuery: Partial<QueryOptions<Client>>): void {
    this.queryWritable.update(
      (previousQuery: Partial<QueryOptions<Client>>) => {
        return {
          ...previousQuery,
          ...securityTypeQuery,
        };
      }
    );
    this.queryChange.emit(this.queryWritable());
  }

  selectedDrawerType: DrawerParams<Client, ClientWithAssignments> | undefined;
  drawerModelChangeEmitter = new EventEmitter<{
    model: ClientWithAssignments;
    prevModel: ClientWithAssignments;
    isValid: boolean;
  }>();

  updateClient(item: Client): void {
    this.grid()?.drawerSettingWritable.update(prevValue => {
      prevValue.edit!.component = UpsertClientComponent;
      prevValue.edit!.title = 'Update clients';
      return prevValue;
    });
    this.grid()?.editActionClick(item);
  }

  manageAssignedClients(item: Client): void {
    this.grid()?.drawerSettingWritable.update(prevValue => {
      prevValue.edit!.component = AssignClientComponent;
      prevValue.edit!.title = 'Manage assigned clients';
      return prevValue;
    });
    this.grid()?.editActionClick(item);
  }

  manageCountryMaterials(item: Client): void {
    this.grid()?.drawerSettingWritable.update(prevValue => {
      prevValue.edit!.component = AssignCountryMaterialComponent;
      prevValue.edit!.title = 'Manage country materials';
      return prevValue;
    });
    this.grid()?.editActionClick(item);
  }

  triggerEmit(
    model: ClientWithAssignments,
    prevModel: ClientWithAssignments,
    closeFn: () => void
  ): void {
    if (
      this.grid()?.drawerSettingWritable().edit!.component ===
      AssignClientComponent
    ) {
      this.assignUser.emit({
        item: model!,
        prevItem: prevModel,
        onSuccess: closeFn,
      });
    } else if (
      this.grid()?.drawerSettingWritable().edit!.component ===
      AssignCountryMaterialComponent
    ) {
      this.assignCountry.emit({
        item: model!,
        prevItem: prevModel,
        onSuccess: closeFn,
      });
    } else {
      this.update.emit({
        item: model!,
        prevItem: prevModel,
        onSuccess: closeFn,
      });
    }
  }
}
